import { useCallback, useEffect, useRef, useState } from 'react';
import { isOfflineDraftRun } from '@/api/runs';
import { getOrderNumberSuffix } from '@/utils/markerUtils';
import { offlineRunPlan } from '@/utils/offlineRunPlan';

// Only facts the operator is confirming belong here. Object identity, route
// geometry, timestamps and other display-only refreshes do not retire intent.
function describe(action, run, orderId, offline, driverMode) {
  if (!run || driverMode || run.status === 'completed') return null;
  const stops = run.populated_stops || [];
  const membership = stops.map(stop => String(stop.order_id)).sort();
  const delivery = stops.map(stop => [String(stop.order_id), stop.order?.status === 'delivered'])
    .sort((a, b) => a[0].localeCompare(b[0]));
  const base = [run.id, run.name, run.status, run.date || null, isOfflineDraftRun(run)];
  if (offline) base.push(offlineRunPlan(run));
  let facts;
  let order;
  if (action === 'commit') {
    if (offline || run.status !== 'draft' || !run.user_id) return null;
    facts = [...base, run.user_id, membership];
  } else if (action === 'uncommit') {
    if (offline || run.status !== 'committed') return null;
    facts = [...base, membership, delivery];
  } else if (action === 'close') {
    if (offline || run.status !== 'committed' || !stops.length
      || !stops.every(stop => stop.order?.status === 'delivered')) return null;
    facts = [...base, membership, delivery];
  } else if (action === 'remove') {
    const stop = stops.find(item => item.order_id === orderId);
    if (offline || run.status !== 'committed' || !stop || stop.order?.status === 'delivered') return null;
    order = {
      orderId,
      recipientName: stop.order?.recipient_name || '',
      orderNumber: stop.order?.internal_order_number
        ? getOrderNumberSuffix(stop.order.internal_order_number) : String(orderId),
    };
    facts = [...base, order, stop.local_stop_id || stop.id || null, stop.order?.status || null];
  } else if (action === 'assign') {
    if (run.status !== 'draft' || delivery.some(([, delivered]) => delivered)) return null;
    facts = [...base, run.user_id || null, isOfflineDraftRun(run), offline];
  } else return null;
  return { facts: JSON.stringify(facts), runId: run.id, runName: run.name,
    stopCount: stops.length, currentUserId: run.user_id, ...order };
}

/** Ephemeral operator intent; not command cancellation or server authority. */
export default function useRunConfirmation(options) {
  const current = useRef(options);
  current.current = options;
  const active = useRef(null);
  const mounted = useRef(true);
  const [shown, setShown] = useState(null);

  const valid = useCallback(intent => {
    const context = current.current;
    if (!mounted.current || !intent || active.current !== intent
      || !context.isScopeCurrent() || !context.isSelectionCurrent(intent.selection)) return false;
    const details = describe(intent.action, context.getRun(intent.runId), intent.orderId,
      context.offline, context.driverMode);
    return details?.facts === intent.facts;
  }, []);

  // Retire on the first observed invalid transition, including A -> B -> A.
  // Merely hiding a stale state value would allow it to revive later.
  if (active.current && !valid(active.current)) active.current = null;

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; active.current = null; };
  }, []);

  const open = useCallback((action, { orderId } = {}) => {
    const context = current.current;
    active.current = null;
    if (!mounted.current || !context.isScopeCurrent()) { setShown(null); return; }
    const run = context.getRun(context.selectedRun?.id);
    const details = describe(action, run, orderId, context.offline, context.driverMode);
    const selection = context.captureSelection();
    if (!details || !selection) { setShown(null); return; }
    const intent = { action, ...details, selection };
    active.current = intent;
    setShown(intent);
  }, []);

  const cancel = useCallback(intent => {
    if (active.current !== intent) return;
    active.current = null;
    setShown(null);
  }, []);

  const consume = useCallback((intent, { userId, unassign = false } = {}) => {
    if (!valid(intent)) {
      if (active.current === intent) { active.current = null; setShown(null); }
      return null;
    }
    if (intent.action === 'assign') {
      if (unassign) {
        if (!intent.currentUserId) return null;
      } else {
        const roster = current.current.driverRoster.getSnapshot();
        if (roster.status !== 'ready' || !roster.drivers.some(driver => driver.id === userId)) return null;
      }
    }
    // The caller dispatches immediately with this identity, without an await.
    active.current = null;
    setShown(null);
    return intent;
  }, [valid]);

  return { intent: shown && valid(shown) ? shown : null, open, cancel, consume };
}
